import type { ModelTypes, UserProgress } from '@zeus/index'
import type { ReportTemplate } from '~/gql/reports/reports'
import { ReportGranularity, ReportPeriodKind } from '@zeus/index'

/**
 * Le formulaire d'un modèle de rapport (#163), en fonctions pures : le hook ne
 * fait que tenir l'état et parler à l'API.
 */
export type ReportForm = {
  name: string
  granularity: ReportGranularity
  /** Clés du catalogue, dans l'ordre du fichier. */
  columns: string[]
  sessionIds: string[]
  groupIds: string[]
  /** Vide : tous les états. */
  state: UserProgress | ''
  period: ReportPeriodKind
  from: string
  to: string
}

/** De quoi démarrer un modèle : l'identité de l'apprenant et l'état au niveau de la ligne. */
const DEFAULT_COLUMNS: Record<ReportGranularity, string[]> = {
  [ReportGranularity.LEARNER]: ['learner.lastName', 'learner.firstName', 'learner.email', 'learner.state', 'learner.lastLogin'],
  [ReportGranularity.ENROLLMENT]: ['learner.lastName', 'learner.firstName', 'learner.email', 'path.name', 'path.state', 'path.completedAt'],
  [ReportGranularity.MODULE]: ['learner.lastName', 'learner.firstName', 'learner.email', 'path.name', 'module.title', 'module.state', 'module.scorePercent', 'module.completedAt'],
}

export function emptyForm(): ReportForm {
  return {
    name: '',
    granularity: ReportGranularity.ENROLLMENT,
    columns: DEFAULT_COLUMNS[ReportGranularity.ENROLLMENT],
    sessionIds: [],
    groupIds: [],
    state: '',
    period: ReportPeriodKind.ALL,
    from: '',
    to: '',
  }
}

export function formFromTemplate(template: ReportTemplate): ReportForm {
  const { filters } = template
  return {
    name: template.name,
    granularity: template.granularity,
    columns: template.columns,
    sessionIds: filters.sessionIds ?? [],
    groupIds: filters.groupIds ?? [],
    state: filters.state ?? '',
    period: filters.period?.kind ?? ReportPeriodKind.ALL,
    from: filters.period?.from ?? '',
    to: filters.period?.to ?? '',
  }
}

/**
 * Changer de granularité garde les colonnes que la nouvelle autorise (le
 * catalogue de l'API, `available`) ; s'il n'en reste aucune, on repart des
 * colonnes par défaut plutôt que d'un fichier vide.
 */
export function withGranularity(form: ReportForm, granularity: ReportGranularity, available: ReadonlySet<string>): ReportForm {
  const kept = form.columns.filter(key => available.has(key))
  return { ...form, granularity, columns: kept.length > 0 ? kept : DEFAULT_COLUMNS[granularity].filter(key => available.has(key)) }
}

/** Cocher ajoute la colonne en fin de fichier ; décocher la retire. */
export function toggleColumn(form: ReportForm, key: string): ReportForm {
  return {
    ...form,
    columns: form.columns.includes(key) ? form.columns.filter(k => k !== key) : [...form.columns, key],
  }
}

/** Déplace une colonne d'un cran (−1 : vers le début du fichier). */
export function moveColumn(form: ReportForm, key: string, delta: -1 | 1): ReportForm {
  const from = form.columns.indexOf(key)
  const to = from + delta
  if (from < 0 || to < 0 || to >= form.columns.length) return form
  const columns = [...form.columns]
  columns.splice(from, 1)
  columns.splice(to, 0, key)
  return { ...form, columns }
}

export function toggleId(ids: string[], id: string): string[] {
  return ids.includes(id) ? ids.filter(i => i !== id) : [...ids, id]
}

/** Ce qui empêche l'enregistrement, par champ ; vide quand le modèle peut partir. */
export function formErrors(form: ReportForm): { name?: true, columns?: true, period?: true } {
  return {
    ...(!form.name.trim() && { name: true as const }),
    ...(form.columns.length === 0 && { columns: true as const }),
    ...(form.period === ReportPeriodKind.RANGE && ((!form.from && !form.to) || (form.from && form.to && form.from > form.to)) && { period: true as const }),
  }
}

export function formToInput(form: ReportForm): ModelTypes['ReportTemplateInput'] {
  const range = form.period === ReportPeriodKind.RANGE
  return {
    name: form.name.trim(),
    granularity: form.granularity,
    columns: form.columns,
    filters: {
      sessionIds: form.sessionIds.length > 0 ? form.sessionIds : undefined,
      groupIds: form.groupIds.length > 0 ? form.groupIds : undefined,
      state: form.state || undefined,
      period: {
        kind: form.period,
        from: range && form.from ? form.from : undefined,
        to: range && form.to ? form.to : undefined,
      },
    },
  }
}
